import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faAngleDown } from "@fortawesome/pro-solid-svg-icons/faAngleDown";
import { faMinusCircle } from "@fortawesome/pro-solid-svg-icons/faMinusCircle";
import { faPlus } from "@fortawesome/pro-solid-svg-icons/faPlus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import InputNumber from "antd/es/input-number";
import Menu from "antd/es/menu";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import sortBy from "lodash-es/sortBy";
import uniq from "lodash-es/uniq";
import { defineMessages, useIntl } from "react-intl";

import { stopEvents } from "@mapmycustomers/shared/util/browser";
import { Button } from "@mapmycustomers/ui";

import localSettings from "@app/config/LocalSettings";
import { setDistanceSettings } from "@app/scene/map/store/actions";
import {
  getLeadFinderCustomDistances,
  getLeadFinderSearchDistance,
} from "@app/scene/map/store/selectors";
import {
  LEAD_FINDER_CUSTOM_DISTANCES_MAX,
  LEAD_FINDER_DEFAULT_DISTANCE_KM,
  LEAD_FINDER_DEFAULT_DISTANCE_MILES,
  LEAD_FINDER_DISTANCES_KM,
  LEAD_FINDER_DISTANCES_MILES,
} from "@app/scene/map/utils/consts";
import { RootState } from "@app/store/rootReducer";
import roundToPrecision from "@app/util/number/roundToPrecision";

import styles from "./SearchRadius.module.scss";

interface Props {
  classes?: {
    dropdown?: string;
    location?: string;
  };
  customDistances: Array<number>;
  radius: number;
  setDistanceSettings: typeof setDistanceSettings;
}

const messages = defineMessages({
  addItem: {
    id: "map.tool.leadFinder.popover.radius.addItem",
    defaultMessage: "Add item",
    description: "Add Item button title",
  },
  alreadyInList: {
    id: "map.tool.leadFinder.popover.radius.alreadyInList",
    defaultMessage: "Distance already available on list",
    description: "Distance already available on list",
  },
  distanceLimit: {
    id: "map.tool.leadFinder.popover.radius.distanceLimit",
    defaultMessage: "Distance must be less than {distance}",
    description: "Distance must be less than X",
  },
  km: {
    id: "map.tool.leadFinder.popover.radius.km",
    defaultMessage: "{distance} km",
    description: "Kilometer unit in custom distance menu",
  },
  limit: {
    id: "map.tool.leadFinder.popover.radius.limit",
    defaultMessage: "Cannot add more than {num} custom distances",
    description: "Cannot add more than X custom distances",
  },
  miles: {
    id: "map.tool.leadFinder.popover.radius.miles",
    defaultMessage: "{distance} {distance, plural, =0 {miles} one {mile} other {miles}}",
    description: "Mile unit in custom distance menu",
  },
  placeholder: {
    id: "map.tool.leadFinder.popover.radius.placeholder",
    defaultMessage: "< {distance}",
    description: "Placeholder for distance input",
  },
  unitKm: {
    id: "map.tool.leadFinder.popover.radius.unit.km",
    defaultMessage: "km",
    description: "Kilometer unit in custom distance menu",
  },
  unitMiles: {
    id: "map.tool.leadFinder.popover.radius.unit.miles",
    defaultMessage: "miles",
    description: "Mile unit in custom distance menu",
  },
});

const SearchRadius: React.FC<Props> = ({
  classes,
  customDistances,
  radius,
  setDistanceSettings,
}) => {
  const intl = useIntl();

  const [value, setValue] = useState<number | undefined>();

  const doesUseSiUnits = localSettings.doesUseSiUnits();
  const defaultDistances = doesUseSiUnits ? LEAD_FINDER_DISTANCES_KM : LEAD_FINDER_DISTANCES_MILES;

  const maxValue = doesUseSiUnits
    ? LEAD_FINDER_DEFAULT_DISTANCE_KM
    : LEAD_FINDER_DEFAULT_DISTANCE_MILES;

  const distanceFormatMessage = doesUseSiUnits ? messages.km : messages.miles;
  const unitMessage = doesUseSiUnits ? messages.unitKm : messages.unitMiles;

  const errorValueMissing = value === undefined || isNaN(value);
  const errorValueTooBig = !errorValueMissing && value > maxValue;
  const errorValueOutOfRange = !errorValueMissing && (value < 0.1 || errorValueTooBig);
  const errorValueIncorrect = errorValueMissing || errorValueOutOfRange;
  const errorValueAlreadyInList = useMemo(
    () => [...customDistances, ...defaultDistances].includes(value ?? 0),
    [customDistances, defaultDistances, value]
  );
  const errorLimitValues = customDistances?.length >= LEAD_FINDER_CUSTOM_DISTANCES_MAX;

  const errorMessage = errorValueTooBig
    ? intl.formatMessage(messages.distanceLimit, {
        distance: intl.formatMessage(distanceFormatMessage, { distance: maxValue }),
      })
    : errorValueAlreadyInList
    ? intl.formatMessage(messages.alreadyInList)
    : errorLimitValues
    ? intl.formatMessage(messages.limit, { num: LEAD_FINDER_CUSTOM_DISTANCES_MAX })
    : null;

  const hasCustomDistances = customDistances?.length > 0;
  const addBtnDisabled = errorValueIncorrect || errorValueAlreadyInList || errorLimitValues;

  const handleClick = useCallback(
    (value) => {
      setDistanceSettings({ searchDistance: parseFloat(value.key) });
      setValue(undefined);
    },
    [setDistanceSettings, setValue]
  );

  const handleDistanceChange = useCallback(
    (value: null | number) => {
      if (value !== null) {
        setValue(roundToPrecision(value, 1));
      }
    },
    [setValue]
  );

  const handleAdd = useCallback(() => {
    if (!addBtnDisabled) {
      setDistanceSettings({
        customDistances: sortBy(uniq([...customDistances, value])),
        searchDistance: value,
      });
    }

    setValue(undefined);
  }, [addBtnDisabled, customDistances, setDistanceSettings, setValue, value]);

  const handleKeyPress = useCallback(
    (event) => {
      if (!addBtnDisabled && event.key === "Enter") {
        stopEvents(event);
        handleAdd();
      }
    },
    [addBtnDisabled, handleAdd]
  );

  const handleRemove = useCallback(
    (item) => {
      const updatedList = sortBy(uniq(customDistances.filter((value) => value !== item)));
      const newDistance = updatedList.find((value) => value > item) ?? maxValue;

      const updatedSettings = {
        customDistances: updatedList,
        ...(item === radius ? { searchDistance: newDistance } : {}),
      };

      setDistanceSettings(updatedSettings);
      setValue(undefined);
    },
    [customDistances, maxValue, radius, setDistanceSettings, setValue]
  );

  return (
    <Dropdown
      overlay={
        <div className={styles.overlay}>
          <Menu className={styles.menu} onClick={handleClick} selectedKeys={[String(radius)]}>
            {defaultDistances.map((distance) => (
              <Menu.Item key={distance}>
                {intl.formatMessage(distanceFormatMessage, { distance })}
              </Menu.Item>
            ))}

            {hasCustomDistances && (
              <>
                <Menu.Divider />
                {customDistances.map((distance) => (
                  <Menu.Item className={styles.menuItem} key={distance}>
                    {intl.formatMessage(distanceFormatMessage, { distance })}

                    <Button
                      className={styles.remove}
                      icon={<FontAwesomeIcon icon={faMinusCircle} />}
                      onClick={(event) => {
                        stopEvents(event);
                        handleRemove(distance);
                      }}
                      type="link"
                    />
                  </Menu.Item>
                ))}
              </>
            )}
          </Menu>

          <div className={styles.custom} onClick={stopEvents}>
            <div className={styles.group}>
              <InputNumber
                bordered={false}
                className={cn({ [styles.error]: errorValueOutOfRange })}
                min={0.1}
                onChange={handleDistanceChange}
                onKeyPress={handleKeyPress}
                placeholder={intl.formatMessage(messages.placeholder, { distance: maxValue })}
                value={value}
              />
              <span className={styles.unit}>{intl.formatMessage(unitMessage)}</span>
            </div>

            <Tooltip
              overlayClassName={styles.tooltip}
              title={errorMessage}
              trigger={errorMessage ? ["hover"] : []}
            >
              <Button className={styles.add} disabled={addBtnDisabled} onClick={handleAdd}>
                <FontAwesomeIcon icon={faPlus} />
                {intl.formatMessage(messages.addItem)}
              </Button>
            </Tooltip>
          </div>
        </div>
      }
      overlayClassName={styles.container}
      placement="bottomLeft"
      trigger={["click"]}
    >
      <Button className={cn(styles.btn, classes?.dropdown)} type="text">
        {intl.formatMessage(distanceFormatMessage, { distance: radius })}
        <FontAwesomeIcon className={styles.arrow} icon={faAngleDown} />
      </Button>
    </Dropdown>
  );
};

const mapStateToProps = (state: RootState) => ({
  customDistances: getLeadFinderCustomDistances(state),
  radius: getLeadFinderSearchDistance(state),
});

const mapDispatchToProps = {
  setDistanceSettings,
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchRadius);
